import { Lightning, Lightning as lng, Registry } from '@lightningjs/sdk';
import { Theme } from '../../config/theme';
import { AtvKeyboardInput } from '../keyboard/AtvKeyboardInput';
import { ScreenSize } from '../../api/ScreenSize';
import { AtvKeyboardService } from '../keyboard/AtvKeyboardService';
import { Button } from './Button';
import { DetailTranslationKeys, PopupTranslationKeys } from '../../api/models/TranslationKeys';

export class CodeInputDialogHelper {
  static getConfirmRentCodeInput(backend, onSuccess, onCancel) {
    return {
      type: CodeInputDialog,
      params: {
        title: backend.translations.forKey(DetailTranslationKeys.detail_enter_pincode),
        confirmText: backend.translations.forKey(DetailTranslationKeys.detail_btn_purchase),
        cancelText: backend.translations.forKey(PopupTranslationKeys.popup_btn_cancel),
        onlyNumbers: true,
        obfuscate: true,
        callbacks: {
          onSuccess,
          onCancel,
        },
      },
    };
  }

  static getCouponCodeInput(backend, onSuccess, onCancel) {
    return {
      type: CodeInputDialog,
      params: {
        title: backend.translations.forKey(DetailTranslationKeys.detail_claimvoucher_title),
        confirmText: backend.translations.forKey(PopupTranslationKeys.popup_btn_ok),
        cancelText: backend.translations.forKey(PopupTranslationKeys.popup_btn_cancel),
        onlyNumbers: false,
        obfuscate: false,
        callbacks: {
          onSuccess,
          onCancel,
        },
      },
    };
  }
}

const Constants = {
  textTop: 400,
  inputTop: 470,
  buttonsTop: 580,
  buttonWidth: ScreenSize.w / 3 / 2,
  buttonsMargin: 32,
  errorTop: 545,
  focus: Theme.Colors.InputFieldColors.focused,
  unfocus: Theme.Colors.InputFieldColors.unfocused,
};

export class CodeInputDialog extends lng.Component {
  static _template() {
    return {
      x: 0,
      y: 0,
      rect: true,
      color: Theme.Colors.Option.Background.Page,
      h: ScreenSize.h,
      w: ScreenSize.w,
      Label: {
        y: Constants.textTop,
        x: w => w / 2,
        mountX: 0.5,
        h: h => h,
        text: {
          text: '',
          ...Theme.Fonts.CodeInput.Title,
        },
      },
      InputField: {
        y: Constants.inputTop,
        mountX: 0.5,
        x: w => w / 2,
        w: 600,
        h: 62,
        type: AtvKeyboardInput,
        passwordMode: true,
        inputColors: {
          focus: Constants.focus,
          unfocus: Constants.unfocus,
        },
        textColor: 0xffffffff,
        cursorColor: 0xffffffff,
        shader: {
          type: Lightning.shaders.RoundedRectangle,
          radius: 6,
        },
        signals: {
          onFinished: '_inputDone',
        },
      },
      ErrorMsg: {
        y: Constants.errorTop,
        x: w => w / 2,
        mountX: 0.5,
        h: h => h,
        text: {
          text: '',
          ...Theme.Fonts.CodeInput.ErrorMsg,
          textAlign: 'center',
        },
      },
      Buttons: {
        y: Constants.buttonsTop,
        x: w => w / 2 - Constants.buttonsMargin / 2,
        w: Constants.buttonWidth * 2 + Constants.buttonsMargin,
        mountX: 0.5,
        Cancel: {
          type: Button,
          buttonMode: 'dialog',
          w: Constants.buttonWidth,
          label: '',
          signals: {
            onActivate: '_handleCancel',
          },
        },
        Confirm: {
          x: Constants.buttonWidth + Constants.buttonsMargin,
          type: Button,
          buttonMode: 'dialog',
          w: Constants.buttonWidth,
          label: '',
          signals: {
            onActivate: '_handleConfirm',
          },
        },
      },
    };
  }

  _init() {
    AtvKeyboardService.init(this);
    this.focusX = 1;
    this.focusY = 0;
    this.errorMsg = '';
  }

  _firstActive() {
    // defer for the keyboard to initialize first
    Registry.setTimeout(() => {
      this.tag('InputField')._handleEnter();
      this._refocus();
    });
  }

  set params(param) {
    this._params = param;
    this.callbacks = param.callbacks;
    this.tag('Label').text.text = param.title;
    this.tag('Confirm').label = param.confirmText;
    this.tag('Cancel').label = param.cancelText;
    if (param.onlyNumbers) {
      this.tag('InputField').numpadMode = true;
    }
    this.tag('InputField').passwordMode = param.obfuscate;
  }

  set errorMsg(val) {
    this.tag('ErrorMsg').visible = !!val;
    this.tag('ErrorMsg').text.text = val;
  }

  /**
   * @param {{onSuccess: function, onCancel: function}} callbacks
   */
  set callbacks(callbacks) {
    this.onSuccess = callbacks.onSuccess;
    this.onCancel = callbacks.onCancel;
  }

  _handleLeft() {
    this.focusX = Math.max(this.focusX - 1, 0);
  }

  _handleRight() {
    this.focusX = Math.min(this.focusX + 1, this.tag('Buttons').children.length - 1);
  }

  _handleDown() {
    this.focusX = 1;
    this.focusY = 1;
  }

  _handleUp() {
    this.focusX = 1;
    this.focusY = 0;
  }

  _handleBack() {
    this.onCancel && this.onCancel();
    this.fireAncestors('$closeDialog');
  }

  _inputDone() {
    this._handleDown();
  }

  _handleConfirm() {
    this.onSuccess && this.onSuccess(this, this.tag('InputField').input);
  }

  _handleCancel() {
    this.onCancel && this.onCancel();
    this.fireAncestors('$closeDialog');
  }

  _getFocused() {
    if (AtvKeyboardService.instance && AtvKeyboardService.instance.isOpen) {
      return AtvKeyboardService.instance.keyboard;
    }
    if (this.focusY === 0) return this.tag('InputField');
    else if (this.focusY === 1) return this.tag('Buttons').children[this.focusX];
  }

  _detach() {
    AtvKeyboardService.removeInstance();
  }
}
